import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TrendUp, TrendDown, Minus, ArrowsClockwise, ChartLine, ArrowUp, ArrowDown, Gauge, Lightning, Spinner, Star
} from "@phosphor-icons/react";

const TF_LABELS = { "1D": "Daily", "1W": "Weekly", "1M": "Monthly" };
const TF_ORDER = ["1D", "1W", "1M"];

function biasColor(bias) {
  if (bias === "bullish") return "text-emerald-400";
  if (bias === "slightly_bullish") return "text-emerald-500/80";
  if (bias === "bearish") return "text-red-400";
  if (bias === "slightly_bearish") return "text-red-500/80";
  return "text-zinc-400";
}

function biasBg(bias) {
  if (bias === "bullish") return "bg-emerald-500/15 border-emerald-700/40";
  if (bias === "slightly_bullish") return "bg-emerald-500/8 border-emerald-800/30";
  if (bias === "bearish") return "bg-red-500/15 border-red-700/40";
  if (bias === "slightly_bearish") return "bg-red-500/8 border-red-800/30";
  return "bg-zinc-800/40 border-zinc-700/40";
}

function biasLabel(bias) {
  return bias.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase());
}

function ScoreGauge({ score, size = 80 }) {
  const radius = (size - 10) / 2;
  const circumference = Math.PI * radius;
  const filled = (score / 100) * circumference;
  const hue = (score / 100) * 120;
  const color = `hsl(${hue}, 70%, 50%)`;

  return (
    <div className="relative" style={{ width: size, height: size / 2 + 10 }}>
      <svg width={size} height={size / 2 + 10} viewBox={`0 0 ${size} ${size / 2 + 10}`}>
        <path
          d={`M 5 ${size / 2 + 5} A ${radius} ${radius} 0 0 1 ${size - 5} ${size / 2 + 5}`}
          fill="none" stroke="#27272a" strokeWidth="6" strokeLinecap="round"
        />
        <path
          d={`M 5 ${size / 2 + 5} A ${radius} ${radius} 0 0 1 ${size - 5} ${size / 2 + 5}`}
          fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference}`}
          style={{ transition: "stroke-dasharray 0.8s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-end justify-center pb-0.5">
        <span className="text-lg font-bold font-data" style={{ color }}>{score}%</span>
      </div>
    </div>
  );
}

function SignalRow({ signal }) {
  const sigColor = signal.signal.includes("bullish") ? "text-emerald-400" :
    signal.signal.includes("bearish") ? "text-red-400" : "text-zinc-400";
  const SigIcon = signal.signal.includes("bullish") ? ArrowUp :
    signal.signal.includes("bearish") ? ArrowDown : Minus;

  return (
    <div className="flex items-center gap-3 py-1.5 border-b border-zinc-800/40 last:border-0" data-testid={`signal-${signal.name.toLowerCase().replace(/\s/g, "-")}`}>
      <SigIcon size={12} weight="bold" className={sigColor} />
      <span className="text-[11px] text-zinc-300 font-medium w-32 shrink-0">{signal.name}</span>
      <span className="text-[10px] font-data text-zinc-500 w-20 shrink-0">{signal.value}</span>
      <span className="text-[10px] text-zinc-500 flex-1">{signal.detail}</span>
      <Badge className={`text-[8px] px-1.5 py-0 h-4 border-0 rounded-sm ${
        signal.signal.includes("bullish") ? "bg-emerald-900/50 text-emerald-400" :
        signal.signal.includes("bearish") ? "bg-red-900/50 text-red-400" :
        signal.signal.includes("trend") ? "bg-amber-900/50 text-amber-400" :
        "bg-zinc-800 text-zinc-400"
      }`}>
        {signal.signal.replace("_", " ").toUpperCase()}
      </Badge>
    </div>
  );
}

function AssetForecast({ asset, isFav, onToggleFav }) {
  const [selectedTf, setSelectedTf] = useState("1D");
  const tfs = asset.timeframes || {};
  const availableTfs = TF_ORDER.filter(tf => tfs[tf]);
  const current = tfs[selectedTf] || tfs[availableTfs[0]];
  const activeTf = tfs[selectedTf] ? selectedTf : availableTfs[0];

  if (!current) {
    return (
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-medium text-zinc-200">{asset.symbol}</span>
          <span className="text-[10px] text-zinc-600">{asset.name}</span>
        </div>
        <p className="text-xs text-zinc-600">Insufficient data for forecast</p>
      </div>
    );
  }

  const forecast = current.forecast;

  return (
    <div className={`border rounded-sm p-4 transition-colors ${biasBg(forecast.bias)}`} data-testid={`forecast-${asset.symbol.toLowerCase()}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-2">
          {onToggleFav && (
            <button
              onClick={onToggleFav}
              data-testid={`fav-toggle-${asset.symbol.toLowerCase()}`}
              className={`mt-0.5 transition-colors ${isFav ? "text-amber-400 hover:text-amber-300" : "text-zinc-600 hover:text-amber-400"}`}
              title={isFav ? "Remove from favorites" : "Add to favorites"}
            >
              <Star size={14} weight={isFav ? "fill" : "regular"} />
            </button>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-zinc-100">{asset.symbol}</span>
              <span className="text-[10px] text-zinc-500">{asset.name}</span>
            </div>
            {current.current_price > 0 && (
              <p className="text-[11px] font-data text-zinc-400 mt-0.5">
                ${current.current_price >= 1 ? current.current_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : current.current_price.toFixed(6)}
              </p>
            )}
          </div>
        </div>
        <ScoreGauge score={forecast.overall} size={72} />
      </div>

      {/* Timeframe tabs */}
      <div className="flex gap-1 mb-3">
        {availableTfs.map(tf => (
          <button
            key={tf}
            onClick={() => setSelectedTf(tf)}
            data-testid={`tf-${asset.symbol.toLowerCase()}-${tf.toLowerCase()}`}
            className={`text-[9px] font-medium px-2.5 py-1 rounded-sm border transition-colors ${
              activeTf === tf
                ? "bg-zinc-700/60 border-zinc-600 text-zinc-200"
                : "bg-transparent border-zinc-800/60 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700"
            }`}
          >
            {TF_LABELS[tf]}
          </button>
        ))}
      </div>

      {/* Bias label */}
      <div className="flex items-center gap-2 mb-2">
        {forecast.bias.includes("bullish") ? <TrendUp size={14} weight="bold" className="text-emerald-400" /> :
         forecast.bias.includes("bearish") ? <TrendDown size={14} weight="bold" className="text-red-400" /> :
         <Minus size={14} weight="bold" className="text-zinc-400" />}
        <span className={`text-xs font-semibold uppercase tracking-wider ${biasColor(forecast.bias)}`}>
          {biasLabel(forecast.bias)}
        </span>
        <span className="text-[9px] text-zinc-600">
          {TF_LABELS[activeTf]} outlook ({current.data_points} data points)
        </span>
      </div>

      {/* Signals */}
      <div className="mt-2">
        {forecast.signals.map((sig, i) => (
          <SignalRow key={i} signal={sig} />
        ))}
      </div>
    </div>
  );
}

export default function Forecasting() {
  const { user, updateWatchlist } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [accuracy, setAccuracy] = useState(null);
  const [accLoading, setAccLoading] = useState(true);
  const [showAll, setShowAll] = useState(false); // user toggle: favorites only vs all

  const watchlist = user?.watchlist || [];
  const hasFavs = watchlist.length > 0;

  const fetchForecasts = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const resp = await axios.get(`${API}/forecast-all`);
      setData(resp.data);
      // Auto-save snapshot
      axios.post(`${API}/forecast-snapshot`).catch(() => {});
    } catch (e) {
      console.error("Failed to fetch forecasts:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchAccuracy = useCallback(async () => {
    try {
      // Evaluate old snapshots first
      await axios.post(`${API}/forecast-evaluate`).catch(() => {});
      const resp = await axios.get(`${API}/forecast-accuracy`);
      setAccuracy(resp.data);
    } catch {}
    setAccLoading(false);
  }, []);

  useEffect(() => { fetchForecasts(); fetchAccuracy(); }, [fetchForecasts, fetchAccuracy]);

  const allForecasts = data?.forecasts || [];
  const computedAt = data?.computed_at;

  // Filter to user's favorites unless showAll is toggled or no favorites exist
  const displayedForecasts = useMemo(() => {
    if (showAll || !hasFavs) return allForecasts;
    return allForecasts.filter(f => watchlist.includes(f.id));
  }, [allForecasts, watchlist, hasFavs, showAll]);

  const toggleFav = async (assetId) => {
    if (!user) return;
    const action = watchlist.includes(assetId) ? "remove" : "add";
    try { await updateWatchlist(assetId, action); } catch {}
  };

  return (
    <div className="p-5 space-y-5 max-w-[1600px]" data-testid="forecasting-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight flex items-center gap-2">
            <ChartLine size={24} weight="bold" className="text-amber-400" />
            Technical Forecasting
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Multi-timeframe probability predictions based on RSI, MACD, Bollinger Bands, Ichimoku, Stochastic & Moving Averages
          </p>
        </div>
        <div className="flex items-center gap-3">
          {user && (
            <div className="flex items-center rounded-sm border border-zinc-800 overflow-hidden" data-testid="favorites-toggle">
              <button
                onClick={() => setShowAll(false)}
                disabled={!hasFavs}
                data-testid="toggle-favorites"
                className={`text-[10px] px-2.5 py-1 flex items-center gap-1 transition-colors ${
                  !showAll && hasFavs ? "bg-amber-500/20 text-amber-300" : "text-zinc-500 hover:text-zinc-300"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <Star size={10} weight={!showAll && hasFavs ? "fill" : "regular"} />
                Favorites {hasFavs && <span className="text-[9px] text-zinc-500">({watchlist.length})</span>}
              </button>
              <button
                onClick={() => setShowAll(true)}
                data-testid="toggle-all"
                className={`text-[10px] px-2.5 py-1 border-l border-zinc-800 transition-colors ${
                  showAll || !hasFavs ? "bg-zinc-800/60 text-zinc-200" : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                All
              </button>
            </div>
          )}
          {computedAt && (
            <span className="text-[10px] text-zinc-600 font-data">
              Updated {new Date(computedAt).toLocaleTimeString()}
            </span>
          )}
          <button
            data-testid="refresh-forecasts-btn"
            onClick={() => fetchForecasts(true)}
            disabled={refreshing}
            className="text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1 border border-zinc-800 rounded-sm flex items-center gap-1.5"
          >
            {refreshing ? <Spinner size={11} className="animate-spin" /> : <ArrowsClockwise size={11} />}
            Refresh
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-[9px] text-zinc-500 border border-zinc-800/40 rounded-sm px-3 py-2 bg-zinc-900/20">
        <span className="flex items-center gap-1"><Gauge size={10} /> Gauge = bullish probability (0-100%)</span>
        <span className="flex items-center gap-1 text-emerald-500">65%+ = Bullish</span>
        <span className="flex items-center gap-1 text-emerald-600">55-64% = Slightly Bullish</span>
        <span className="flex items-center gap-1 text-zinc-400">46-54% = Neutral</span>
        <span className="flex items-center gap-1 text-red-600">36-45% = Slightly Bearish</span>
        <span className="flex items-center gap-1 text-red-500">0-35% = Bearish</span>
      </div>

      {/* Accuracy Tracker */}
      {!accLoading && accuracy && accuracy.overall_accuracy !== null && (
        <div className="border border-amber-500/20 rounded-sm p-4 bg-zinc-900/20" data-testid="accuracy-panel">
          <div className="flex items-center gap-2 mb-3">
            <Gauge size={14} className="text-amber-400" />
            <span className="text-[11px] font-medium text-zinc-300 uppercase tracking-wider">Forecast Accuracy Tracker</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-2.5 text-center">
              <span className="text-[8px] uppercase tracking-wider text-zinc-500 block mb-0.5">Overall</span>
              <span className="text-xl font-bold font-data text-amber-400">{accuracy.overall_accuracy}%</span>
            </div>
            <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-2.5 text-center">
              <span className="text-[8px] uppercase tracking-wider text-zinc-500 block mb-0.5">Total Predictions</span>
              <span className="text-lg font-bold font-data text-zinc-200">{accuracy.total_predictions}</span>
            </div>
            <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-2.5 text-center">
              <span className="text-[8px] uppercase tracking-wider text-zinc-500 block mb-0.5">Correct</span>
              <span className="text-lg font-bold font-data text-emerald-400">{accuracy.correct_predictions}</span>
            </div>
            <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-2.5 text-center">
              <span className="text-[8px] uppercase tracking-wider text-zinc-500 block mb-0.5">Snapshots</span>
              <span className="text-lg font-bold font-data text-zinc-300">{accuracy.snapshots_evaluated}</span>
            </div>
          </div>
          {/* By asset breakdown */}
          {Object.keys(accuracy.by_asset || {}).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(accuracy.by_asset).map(([sym, stats]) => (
                <div key={sym} className="text-[10px] px-2 py-1 rounded-sm border border-zinc-800/60 bg-zinc-950/40">
                  <span className="text-zinc-400 font-medium">{sym}</span>
                  <span className={`ml-1.5 font-data font-bold ${stats.accuracy >= 55 ? "text-emerald-400" : stats.accuracy >= 45 ? "text-zinc-300" : "text-red-400"}`}>
                    {stats.accuracy}%
                  </span>
                  <span className="text-zinc-600 ml-1">({stats.total})</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {!accLoading && accuracy && accuracy.overall_accuracy === null && (
        <div className="border border-zinc-800/40 rounded-sm p-3 bg-zinc-900/20 text-center" data-testid="accuracy-pending">
          <p className="text-xs text-zinc-500">{accuracy.message || "Tracking predictions. Accuracy data will appear after the first evaluation period."}</p>
        </div>
      )}

      {!user && (
        <div className="border border-amber-500/25 rounded-sm p-4 bg-amber-900/10 flex items-center gap-3" data-testid="login-cta">
          <Star size={16} weight="fill" className="text-amber-400 shrink-0" />
          <div className="flex-1">
            <p className="text-xs text-zinc-200 font-medium">Sign in to pick your favorite assets.</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">Once signed in, star the assets you care about and they'll show up here first.</p>
          </div>
        </div>
      )}

      {user && !hasFavs && !showAll && (
        <div className="border border-amber-500/25 rounded-sm p-4 bg-amber-900/10" data-testid="empty-favorites-cta">
          <div className="flex items-center gap-3 mb-2">
            <Star size={16} weight="fill" className="text-amber-400 shrink-0" />
            <p className="text-xs text-zinc-200 font-medium">Pick your favorite assets</p>
          </div>
          <p className="text-[10px] text-zinc-500 leading-relaxed">
            Tap the <Star size={9} weight="fill" className="inline text-amber-400 mx-0.5" /> on any asset below to favorite it. Your favorites will show up here first every time you visit Technical Forecasting.
          </p>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-64 bg-zinc-900/60" />
          ))}
        </div>
      ) : displayedForecasts.length === 0 ? (
        <div className="border border-zinc-800/60 rounded-sm p-8 text-center bg-zinc-900/20" data-testid="no-favorites-empty">
          <Star size={24} weight="duotone" className="text-zinc-600 mx-auto mb-2" />
          <p className="text-xs text-zinc-400 mb-1">No favorites yet</p>
          <p className="text-[10px] text-zinc-600 mb-3">Switch to "All" to see every forecast and star the ones you want to track.</p>
          <button
            onClick={() => setShowAll(true)}
            data-testid="show-all-btn"
            className="text-[11px] px-3 py-1.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-colors"
          >
            Show all assets
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {displayedForecasts.map(asset => (
            <AssetForecast
              key={asset.id}
              asset={asset}
              isFav={watchlist.includes(asset.id)}
              onToggleFav={user ? () => toggleFav(asset.id) : null}
            />
          ))}
        </div>
      )}

      <p className="text-[9px] text-zinc-700 text-center pt-2">
        Technical analysis is probabilistic, not predictive. Past performance does not guarantee future results. Always do your own research.
      </p>
    </div>
  );
}
